import React, { useEffect, useState } from 'react';

import type {
  BulkEmailDTO,
  SearchRequestDTO_bulkEmailSearchRequestDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

import AddNewButton from '../../../components/common/AddButton';
import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import Drawer from '../../../components/common/Drawer';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import BulkEmailTable from '../../../components/tables/BulkEmailTable';
import { useBulkEmailApi } from '../../../hooks/useBulkEmailApi';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import Swal from 'sweetalert2';

const BulkEmailList: React.FC = () => {
  const [bulkEmails, setBulkEmails] = useState<BulkEmailDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [subjectSearch, setSubjectSearch] = useState('');

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedBulkEmail, setSelectedBulkEmail] = useState<BulkEmailDTO | null>(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Bulk Emails', href: '#' },
  ];

  const { search, processBulkEmail, remove, sendTestEmail } = useBulkEmailApi();
  const fetchBulkEmails = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    const searchRequest: SearchRequestDTO_bulkEmailSearchRequestDTO = {
      filters: {
        subject: subjectSearch || '',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'CreatedUtc',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await search(searchRequest);
      if (response && response.success && response.data) {
        const totalRecords = response.data?.totalCount ?? 0;

        setBulkEmails((response.data?.results as BulkEmailDTO[]) || []);
        setTotalRecords(totalRecords);
        setTotalPages(Math.ceil(totalRecords / size));
      }
    } catch (error) {
      console.error('Error fetching bulk emails:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBulkEmails(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBulkEmails(1, pageSize);
  };

  const handleClearSearch = (): void => {
    setSubjectSearch('');
    fetchBulkEmails(1, pageSize, {
      subject: '',
    });
  };

  const handleProcessSend = async (dto: BulkEmailDTO): Promise<void> => {
    try {
      await processBulkEmail(dto.id || '');
      await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
    } catch (error) {
      console.error('Error processing bulk email:', error);
    }
  };

  const handleDelete = async (dto: BulkEmailDTO): Promise<void> => {
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }

    try {
      await remove(dto.id || '');
      await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

    const handleOpenTestDrawer = (dto: BulkEmailDTO): void => {
    setSelectedBulkEmail(dto);
    setTestEmailAddress('');
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = (): void => {
    setIsDrawerOpen(false);
    setSelectedBulkEmail(null);
    setTestEmailAddress('');
  };

  const handleSendTestEmail = async (): Promise<void> => {
    if (!selectedBulkEmail || !testEmailAddress) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please enter a valid email address.',
      });
      return;
    }

    try {
      await sendTestEmail(selectedBulkEmail.id || '', testEmailAddress);
      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Test email sent successfully to ${testEmailAddress}`,
      });
      handleCloseDrawer();
    } catch (error) {
      console.error('Error sending test email:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to send test email. Please try again.',
      });
    }
  };


  return (
    <AdminLayout
      activeMenuItem="/admin/bulkemails"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      <Card
        title="Manage Bulk Emails"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/bulkemails/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="subjectSearch"
              label="Subject"
              value={subjectSearch}
              onChange={(e) => setSubjectSearch(e.target.value)}
              placeholder="Search by subject"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <BulkEmailTable
          emails={bulkEmails}
          handleEdit={(dto) => navigate(`/admin/bulkemails/${dto.id}`)}
          handleDelete={handleDelete}
          handleProcessSend={handleProcessSend}
          handleTestEmail={handleOpenTestDrawer}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
      
      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={handleCloseDrawer}
        title="Send Test Email"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Send a test version of this bulk email to preview how it will look.
          </p>
          
          {selectedBulkEmail && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Subject: {selectedBulkEmail.subject}
              </p>
            </div>
          )}

          <FormField
            id="testEmailAddress"
            label="Test Email Address"
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            placeholder="Enter email address"
            textCase='lowercase'
            required
          />

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleSendTestEmail}
              disabled={!testEmailAddress}
            >
              Send Test Email
            </Button>
            <Button
              variant="secondary"
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Drawer>
    </AdminLayout>
  );
};

export default BulkEmailList;
